/**
 * Client-side Firestore API for the `travel_trips` collection (RK-3).
 *
 * Muster: `expensesApi.ts` (owner-scoped reads/writes, `getEffectiveScopeUid()`, best-effort
 * append-only Audit-Log, `undefined`-Feld-Strip vor jedem Firestore-Write). Fachliche
 * Grundlage: architecture-reisekosten.md §3.3 (Client-API-Verträge) und §4.1 (Datenmodell).
 *
 * Trust-Boundary (§3.3, V13 — bewusst akzeptiert, KEINE Lücke): die Beträge in
 * `settlement.calc` sind client-berechnet/client-trusted, identisch zum bestehenden
 * `expenses`-Betragsmodell. Serverseitig kontrolliert (Firestore-Rules §7) ist ausschließlich
 * der LEBENSZYKLUS: `create` nur als `status=='draft'`, `settled` ist unveränderlich bis auf
 * die Cancel-Transition mit Feld-Allowlist. Diese API härtet den Lebenszyklus zusätzlich
 * client-seitig (Status-Vorabprüfungen für UX, `settleTravelTrip` erzwingt zusätzlich einen
 * eigenen Integer-Cent-Guard VOR dem Write, §3.3c) — ersetzt aber nicht die Rules.
 *
 * Index-Bedarf: KEINER. Einzige Query ist `where('userId','==',scopeUid)` ohne `orderBy`
 * (§4.1) — Sortierung erfolgt client-seitig nach `returnDateISO` (neueste zuerst).
 *
 * @module travelApi
 */

import {
  collection,
  addDoc,
  doc,
  getDoc,
  getDocs,
  updateDoc,
  deleteDoc,
  query,
  where,
  serverTimestamp,
} from 'firebase/firestore';
import { db, auth } from '../firebaseConfig';
import {
  CreateTravelTripInput,
  TravelTrip,
  TravelSettlement,
  TravelSettlementLinkedExpense,
  UpdateTravelTripPatch,
} from '../types/travelTypes';
import { getExpense } from './expensesApi';
import { AuditService } from '../services/workflow/auditService';
import { getEffectiveScopeUid } from '../utils/tenantScope';
import logger from '../utils/logger';
import {
  validateCalcSnapshotCents,
  validateCreateTravelTripInput,
  validateTripForSettle,
  validateUpdateTravelTripPatch,
} from '../utils/travel/travelValidation';
import { calculateTravelAllowances, TravelCalcInput } from '../utils/travel/travelAllowance';
import { TRAVEL_RATES } from '../utils/travel/travelRates';
import { getForeignRatesForUser } from '../config/travelExpensesAccess';
import type { Tier } from '../config/entitlements';
import { TravelApiError } from './travelApiError';

const TRAVEL_TRIPS_COLLECTION = 'travel_trips';

// ─── Internal helpers ─────────────────────────────────────────────────────────

/**
 * Returns the effective scope UID for owner-scoped queries.
 *
 * Fail-closed: throws if no user is signed in (Golden Rule — never an anonymous scope).
 * Mirrors `expensesApi.requireCurrentUser()`: uses {@link getEffectiveScopeUid} so an active
 * Advisor-Mandanten-Scope (M11) transparently targets the mandate's `travel_trips` instead of
 * the advisor's own (empty) data.
 */
function requireCurrentUser(): string {
  const user = auth.currentUser;
  if (!user) {
    throw new TravelApiError(
      'notAuthenticated',
      'travelApi: No authenticated user. Sign in before accessing travel trips.',
    );
  }
  return getEffectiveScopeUid();
}

/**
 * Writes a best-effort, append-only audit log entry for a `travel_trip` mutation
 * (§3.3 Pflicht a — Muster `writeExpenseAudit`).
 *
 * Runs AFTER the main mutation succeeded and is wrapped in its own try/catch: a failed audit
 * write is logged as a warning but never throws, so an audit glitch can never roll back or
 * hide a persisted travel-trip mutation (Golden Rule).
 *
 * NON-BLOCKING VERTRAG (§3.3 Pflicht a: „best-effort/non-blocking"): dieser Helfer MUSS von den
 * Mutations-Funktionen fire-and-forget aufgerufen werden (`void writeTravelAudit(...)`), NIEMALS
 * `await`ed. Grund (Bug-Fix, Live-Prod): ein `await` fängt zwar eine REJECTION ab (try/catch),
 * NICHT aber ein Promise, das gar nicht erst auflöst. Die `AUDIT_LOGS`-`addDoc`-Schreiboperation
 * kann unter bestimmten Netzzuständen (beobachtet nach vorherigem httpsCallable-Route-Lookup:
 * kongestionierter WebChannel-Write-Stream) den Server-Ack für zig Sekunden bis unbegrenzt
 * verzögern — die HAUPT-Mutation (`addDoc`/`updateDoc` des Trips) ist zu diesem Zeitpunkt bereits
 * committed. Ein `await` hier hielte die Mutations-Promise offen, obwohl die Reise längst
 * persistiert ist (Symptom: „Wird gespeichert…" hängt >30 s, Reise erscheint aber nach Reload).
 * Fire-and-forget entkoppelt den Audit-Trail (der ohnehin best-effort ist) vom Auflösen der
 * Mutation, sodass der Speichervorgang deterministisch auflöst.
 */
async function writeTravelAudit(
  uid: string,
  action: 'create' | 'update' | 'settle' | 'cancel' | 'delete',
  targetId: string,
  details: Record<string, unknown>,
): Promise<void> {
  try {
    await AuditService.addAuditLog(uid, action, 'travel_trip', targetId, {
      source: 'client',
      ...details,
    });
  } catch (err) {
    logger.warn('travelApi: travel_trip audit-log write failed (non-blocking)', err);
  }
}

/**
 * Fail-closed guard: throws if any (possibly nested) value in `obj` is `undefined`.
 *
 * Muster `expensesApi.assertNoUndefinedValues` (nicht exportiert dort, daher hier lokal
 * dupliziert — Repo-Konvention: jedes `*Api.ts`-Modul trägt seine eigene Kopie). Grund: die
 * Firestore Web SDK ist OHNE `ignoreUndefinedProperties` initialisiert (`firebaseConfig.ts`),
 * `addDoc`/`updateDoc` lehnen jeden `undefined`-Feldwert mit einer opaken
 * "Unsupported field value: undefined"-Fehlermeldung ab (V10, Golden Rule).
 *
 * Traversal-Vertrag: rekursiert NUR in reine Objekte (`prototype === Object.prototype`) und
 * Arrays. Firestore-`FieldValue`-Sentinels (`serverTimestamp()`) sind Klasseninstanzen mit
 * abweichendem Prototyp und werden als opake Blätter behandelt — sie sind legitime Werte und
 * dürfen NICHT durchlaufen werden. `null` ist ein gültiger Firestore-Wert und passiert.
 */
function assertNoUndefinedValues(obj: Record<string, unknown>, path = ''): void {
  for (const [key, value] of Object.entries(obj)) {
    const fieldPath = path ? `${path}.${key}` : key;

    if (value === undefined) {
      throw new Error(
        `travelApi: field '${fieldPath}' is undefined — omit the key instead of writing ` +
          'undefined (Firestore rejects undefined field values).',
      );
    }

    if (value === null) continue;

    if (Array.isArray(value)) {
      value.forEach((item, index) => {
        if (item === undefined) {
          throw new Error(
            `travelApi: field '${fieldPath}[${index}]' is undefined — omit the entry instead ` +
              'of writing undefined (Firestore rejects undefined field values).',
          );
        }
        if (
          typeof item === 'object' &&
          item !== null &&
          Object.getPrototypeOf(item) === Object.prototype
        ) {
          assertNoUndefinedValues(item as Record<string, unknown>, `${fieldPath}[${index}]`);
        }
      });
      continue;
    }

    if (typeof value === 'object' && Object.getPrototypeOf(value) === Object.prototype) {
      assertNoUndefinedValues(value as Record<string, unknown>, fieldPath);
    }
  }
}

/**
 * Baut das deterministische Beleg-Label einer abgerechneten Reise (E5, §11 verbindliches
 * Soll-Tripel): `'RK-' + YYYYMMDD(returnDateISO) + '-' + tripId.slice(0,6)` — Groß-/
 * Kleinschreibung der Doc-ID UNVERÄNDERT, Gesamtlänge exakt 18 Zeichen. Kein atomarer Zähler
 * nötig (E5-Begründung: Eindeutigkeit + Nachvollziehbarkeit trägt der Trip-Datensatz selbst).
 *
 * Beispiel (§11): `returnDateISO='2026-03-14'`, `tripId='aB3xYz9QmT4kLp0Wc2Ef'` ⇒
 * `'RK-20260314-aB3xYz'`.
 */
function buildBelegLabel(returnDateISO: string, tripId: string): string {
  const yyyymmdd = returnDateISO.replace(/-/g, '');
  return `RK-${yyyymmdd}-${tripId.slice(0, 6)}`;
}

/**
 * Baut den Settle-Zeit-Snapshot der verknüpften Ist-Kosten (RK-22, E19, §14.3) — fail-closed.
 *
 * Lädt VOR jedem Write je `linkedExpenseIds`-Eintrag die Ausgabe per EINZEL-`getExpense(id)`
 * (bewusst NICHT `where(documentId(),'in')` — bekannte Firestore-Permission-Falle bei nicht
 * (mehr) existenten IDs, [[firestore-documentid-in-permission-gotcha]]). Fail-closed-Kette:
 *
 *   1. Ist eine Ausgabe NICHT ladbar (`getExpense` liefert `null`) ODER NICHT `status==='booked'`
 *      ⇒ sie kommt auf die Unavailable-Liste. Ist die Liste am Ende nicht leer, wird
 *      `TravelApiError 'linkedExpenseUnavailable'` mit der VOLLSTÄNDIGEN ID-Liste geworfen —
 *      KEIN Write, KEIN Teil-Write, KEIN Silent-Drop (der Nutzer entfernt die Verknüpfung im
 *      noch editierbaren Draft). Alle IDs werden erst vollständig gesammelt, dann geworfen.
 *   2. Erst wenn ALLE Ausgaben verfügbar sind, wird der Snapshot gebaut; `grossCents` jeder
 *      Ausgabe wird an der Geld-Grenze auf `Number.isFinite` UND `Number.isInteger` geprüft
 *      (Golden Rule) — Verletzung ⇒ `TravelApiError 'snapshotInvalid'`, ebenfalls KEIN Write.
 *
 * `getExpense` wirft bei Cross-Scope-Zugriff selbst (fremder Owner) — dieser Fehler propagiert
 * bewusst (fail-closed: kein Write), da es ein Datenintegritäts-/Berechtigungsproblem ist und
 * keine reguläre „Verknüpfung nicht verfügbar"-Fachlage.
 *
 * @returns Snapshot-Array (ggf. leer, wenn keine Verknüpfungen existieren).
 * @throws {TravelApiError} `linkedExpenseUnavailable` / `snapshotInvalid` — in beiden Fällen
 *   darf der Aufrufer NICHTS schreiben.
 */
async function buildLinkedExpensesSnapshot(
  linkedExpenseIds: readonly string[] | null | undefined,
): Promise<TravelSettlementLinkedExpense[]> {
  if (!Array.isArray(linkedExpenseIds) || linkedExpenseIds.length === 0) {
    return [];
  }

  // Phase 1: alle Ausgaben laden (Einzel-getExpense je ID, KEIN documentId()-in-Query).
  const loaded = await Promise.all(
    linkedExpenseIds.map(async (expenseId) => ({ expenseId, expense: await getExpense(expenseId) })),
  );

  // Phase 2: Verfügbarkeit prüfen — VOLLSTÄNDIGE Liste sammeln, dann fail-closed werfen.
  const unavailableExpenseIds = loaded
    .filter(({ expense }) => expense === null || expense.status !== 'booked')
    .map(({ expenseId }) => expenseId);

  if (unavailableExpenseIds.length > 0) {
    throw new TravelApiError(
      'linkedExpenseUnavailable',
      `travelApi.settleTravelTrip: mindestens eine verknüpfte Ausgabe ist nicht ladbar oder ` +
        `nicht gebucht ('booked') — Snapshot wird NICHT geschrieben. Betroffene IDs: ` +
        `${unavailableExpenseIds.join(', ')}.`,
      { unavailableExpenseIds },
    );
  }

  // Phase 3: Snapshot bauen — `grossCents`-Integer-Guard je Eintrag an der Geld-Grenze.
  return loaded.map(({ expenseId, expense }): TravelSettlementLinkedExpense => {
    // Nach Phase 2 strukturell garantiert non-null + 'booked'; erneut narrowen (Defense-in-Depth).
    if (expense === null || expense.status !== 'booked') {
      throw new TravelApiError(
        'linkedExpenseUnavailable',
        `travelApi.settleTravelTrip: verknüpfte Ausgabe ${expenseId} unerwartet nicht verfügbar.`,
        { unavailableExpenseIds: [expenseId] },
      );
    }

    const { grossCents } = expense;
    if (!Number.isFinite(grossCents) || !Number.isInteger(grossCents)) {
      throw new TravelApiError(
        'snapshotInvalid',
        `travelApi.settleTravelTrip: verknüpfte Ausgabe ${expenseId} hat einen ungültigen ` +
          `grossCents-Wert (fail-closed, kein Integer/finite) — Snapshot wird NICHT geschrieben.`,
      );
    }

    return {
      expenseId,
      supplierName: expense.supplierName ?? '',
      invoiceNumber: expense.invoiceNumber ?? null,
      expenseDateISO: expense.expenseDate ?? null,
      grossCents,
    };
  });
}

// ─── getTravelTripsForUser ──────────────────────────────────────────────────────

/**
 * Loads all `travel_trips` documents for the effective scope UID.
 *
 * KEIN `orderBy` (kein Index-Bedarf, §4.1) — Sortierung erfolgt client-seitig nach
 * `returnDateISO` absteigend (neueste Reise zuerst, Muster `getExpensesForUser`).
 *
 * @returns Array of TravelTrip objects (may be empty).
 * @throws {Error} If not authenticated or the Firestore query fails.
 */
export async function getTravelTripsForUser(): Promise<TravelTrip[]> {
  const uid = requireCurrentUser();

  const q = query(collection(db, TRAVEL_TRIPS_COLLECTION), where('userId', '==', uid));
  const snapshot = await getDocs(q);
  const trips = snapshot.docs.map((d): TravelTrip => ({ id: d.id, ...d.data() } as TravelTrip));

  return trips.sort((a, b) => (a.returnDateISO < b.returnDateISO ? 1 : a.returnDateISO > b.returnDateISO ? -1 : 0));
}

// ─── getTravelTrip ──────────────────────────────────────────────────────────────

/**
 * Fetches a single travel trip by document ID, scoped to the effective scope UID.
 *
 * @param id - Firestore document ID.
 * @returns The TravelTrip or `null` if not found.
 * @throws {Error} If not authenticated, the document belongs to a different scope, or the
 *   Firestore read fails.
 */
export async function getTravelTrip(id: string): Promise<TravelTrip | null> {
  const uid = requireCurrentUser();

  const docRef = doc(db, TRAVEL_TRIPS_COLLECTION, id);
  const snapshot = await getDoc(docRef);

  if (!snapshot.exists()) {
    return null;
  }

  const data = snapshot.data() as TravelTrip;

  // Client-side ownership guard (belt + suspenders alongside Firestore Rules, Muster getExpense).
  if (data.userId !== uid) {
    throw new TravelApiError(
      'crossScope',
      'travelApi.getTravelTrip: document belongs to a different user/scope. Cross-user reads are denied.',
    );
  }

  return { ...data, id: snapshot.id };
}

// ─── createTravelTrip ───────────────────────────────────────────────────────────

/**
 * Creates a new travel trip document in `travel_trips`, status `'draft'`.
 *
 * Validiert die Eingabe VOR dem Write via {@link validateCreateTravelTripInput} (HH:mm,
 * lückenlose `days[]`-Deckung, `overnight` des letzten Tages, `mileage`). `userId`/`createdBy`
 * werden immer aus dem effektiven Scope gesetzt (nie aus dem Aufrufer übernommen, M0-Scope).
 *
 * @param input - Trip-Felder nach §4.1 `CreateTravelTripInput`.
 * @returns Firestore document ID of the newly created trip.
 * @throws {Error} If not authenticated, validation fails, or the Firestore write fails.
 */
export async function createTravelTrip(input: CreateTravelTripInput): Promise<string> {
  const uid = requireCurrentUser();

  const validation = validateCreateTravelTripInput(input);
  if (!validation.valid) {
    throw new TravelApiError(
      'validationFailed',
      `travelApi.createTravelTrip: Validierung fehlgeschlagen — ${validation.issues.map((i) => i.detail).join('; ')}`,
      { issues: validation.issues },
    );
  }

  const docData: Omit<TravelTrip, 'id'> = {
    ...input,
    userId: uid, // always overwrite — M0 scope
    createdBy: uid,
    status: 'draft',
    settlement: null,
    createdAt: serverTimestamp() as TravelTrip['createdAt'],
  };

  // Fail-closed BEFORE the write: a materialised `undefined` key would make the Firestore
  // SDK reject the whole addDoc with an opaque error (V10, see assertNoUndefinedValues doc).
  assertNoUndefinedValues(docData as unknown as Record<string, unknown>);

  const colRef = collection(db, TRAVEL_TRIPS_COLLECTION);
  const docRef = await addDoc(colRef, docData);

  // Fire-and-forget (best-effort, non-blocking — s. writeTravelAudit-Doc): der Trip ist ab hier
  // persistiert; ein `await` auf den ggf. stallenden AUDIT_LOGS-Write darf die create-Promise NIE
  // offen halten. Ohne dieses Entkoppeln blieb „Anlegen" bei km-Reisen >30 s hängen.
  void writeTravelAudit(uid, 'create', docRef.id, {
    purpose: docData.purpose,
    destinationCity: docData.destinationCity,
  });

  return docRef.id;
}

// ─── updateTravelTrip ───────────────────────────────────────────────────────────

/**
 * Partially updates a `draft` travel trip.
 *
 * `UpdateTravelTripPatch` schließt `settlement`/`status`/Cancel-Felder bereits TYPSEITIG aus
 * (§3.3d) — sie sind ausschließlich über {@link settleTravelTrip}/{@link cancelTravelTrip}
 * setzbar. Diese Funktion prüft zusätzlich VORAB (für UX; die Firestore-Rules erzwingen es
 * ohnehin serverseitig, §3.3), dass der Trip aktuell `status === 'draft'` ist — ein Update auf
 * einem `settled`/`cancelled`-Trip wird von den Rules abgelehnt, da `UpdateTravelTripPatch`
 * `status` nie mit-sendet und der zweite Rule-Zweig (Cancel-Transition) damit nie greift.
 *
 * @param id - Firestore document ID of the trip to update.
 * @param patch - Partial fields, typseitig ohne `settlement`/`status`/Cancel-Felder.
 * @throws {Error} If not authenticated, the trip does not exist, is not `draft`, validation
 *   fails, or the Firestore write fails.
 */
export async function updateTravelTrip(id: string, patch: UpdateTravelTripPatch): Promise<void> {
  const uid = requireCurrentUser();

  const validation = validateUpdateTravelTripPatch(patch);
  if (!validation.valid) {
    throw new TravelApiError(
      'validationFailed',
      `travelApi.updateTravelTrip: Validierung fehlgeschlagen — ${validation.issues.map((i) => i.detail).join('; ')}`,
      { issues: validation.issues },
    );
  }

  const current = await getTravelTrip(id);
  if (current === null) {
    throw new TravelApiError('notFound', `travelApi.updateTravelTrip: Reise ${id} nicht gefunden.`);
  }
  if (current.status !== 'draft') {
    throw new TravelApiError(
      'wrongStatus',
      `travelApi.updateTravelTrip: nur Reisen im Status 'draft' können bearbeitet werden ` +
        `(aktueller Status: '${current.status}').`,
    );
  }

  assertNoUndefinedValues(patch as Record<string, unknown>);

  const docRef = doc(db, TRAVEL_TRIPS_COLLECTION, id);
  await updateDoc(docRef, patch as Partial<TravelTrip>);

  // Fire-and-forget (best-effort, non-blocking — s. writeTravelAudit-Doc).
  void writeTravelAudit(uid, 'update', id, { changedFields: Object.keys(patch) });
}

// ─── settleTravelTrip ───────────────────────────────────────────────────────────

/**
 * Rechnet eine `draft`-Reise ab: recompute via {@link calculateTravelAllowances}
 * (`ok:true` PFLICHT), schreibt den `settlement`-Snapshot + `status:'settled'`.
 *
 * Ablauf (§3.3, Berechnungsregeln §3.2):
 *   1. Lädt den vollständigen, gespeicherten Trip (nie den Client-Zustand blind vertrauen).
 *   2. Struktur-Validierung ({@link validateTripForSettle}: `purpose`/`destinationCity`
 *      non-empty, HH:mm, `days[]`-Deckung, `mileage`).
 *   3. Recompute über den Berechnungskern (RK-2/RK-14) mit der aktuellen `TRAVEL_RATES`-
 *      Konfiguration UND (falls das Sub-Flag-Gate offen ist) den Auslands-Sätzen (§13.7).
 *   4. `ok:false` ⇒ throw mit dem `TravelCalcErrorCode` in der Fehlermeldung.
 *   5. `ok:true` ⇒ PFLICHT (§3.3c, zusätzlich zum Kern-internen Guard Regel 8):
 *      {@link validateCalcSnapshotCents} auf dem KOMPLETTEN Snapshot — Defense-in-Depth,
 *      damit strukturell nie ein `NaN`/Float in einen Write gelangen kann (V12).
 *   6. Beleg-Label bauen (E5), Snapshot + `status:'settled'` schreiben.
 *
 * Auslands-Zweig (RK-15, V19 Gate-Parity): `options.foreignRates` wird AUSSCHLIESSLICH über
 * {@link getForeignRatesForUser} bezogen — denselben Entscheidungs-Helfer, den die Live-Vorschau
 * (`TripSettlementSummary`) nutzt. `isPremium` ist hier optional (additiv, Default `false`),
 * weil dieses Modul (`travelApi`) selbst keinen React-Auth-Kontext hat — der Aufrufer
 * (`TravelExpensesPage`, `useAuth()`) reicht ihn durch. Fehlt er, bleibt das Sub-Flag-Gate
 * fail-closed zu (kein Premium ⇒ kein Auslands-Zweig, unabhängig von der Beta-Allowlist-Prüfung
 * — die läuft weiterhin über die tatsächliche UID).
 *
 * @param id - Firestore document ID of the trip to settle.
 * @param options - Optional. `tier`: effektiver Tier des aktuellen Nutzers (für das
 *   Auslands-Sub-Flag-Gate, §13.7). Weggelassen ⇒ `'free'` (fail-closed).
 * @throws {Error} If not authenticated, the trip does not exist, is not `draft`, structural
 *   validation fails, the calculation core returns `ok:false`, the resulting snapshot fails
 *   the Integer-Cent-Guard, or the Firestore write fails.
 */
export async function settleTravelTrip(id: string, options?: { tier?: Tier }): Promise<void> {
  const uid = requireCurrentUser();

  const trip = await getTravelTrip(id);
  if (trip === null) {
    throw new TravelApiError('notFound', `travelApi.settleTravelTrip: Reise ${id} nicht gefunden.`);
  }
  if (trip.status !== 'draft') {
    throw new TravelApiError(
      'wrongStatus',
      `travelApi.settleTravelTrip: nur Reisen im Status 'draft' können abgerechnet werden ` +
        `(aktueller Status: '${trip.status}').`,
    );
  }

  const structuralValidation = validateTripForSettle(trip);
  if (!structuralValidation.valid) {
    throw new TravelApiError(
      'validationFailed',
      `travelApi.settleTravelTrip: Reise ist nicht abrechenbar — ` +
        `${structuralValidation.issues.map((i) => i.detail).join('; ')}`,
      { issues: structuralValidation.issues },
    );
  }

  const calcInput: TravelCalcInput = {
    departureDateISO: trip.departureDateISO,
    departureTime: trip.departureTime,
    returnDateISO: trip.returnDateISO,
    returnTime: trip.returnTime,
    days: trip.days,
    mileage: trip.mileage,
    isFirstWorkplace: trip.isFirstWorkplace,
  };
  // V19 Gate-Parity: dieselbe Entscheidung wie die Live-Vorschau — die Beta-/Flag-Prüfung läuft
  // über die ECHTE Firebase-Auth-UID (nicht `uid`/`requireCurrentUser()`, das für einen aktiven
  // Advisor-Mandanten-Scope den MANDANTEN liefert; Advisors sind für `settleTravelTrip` ohnehin
  // read-only ausgeschlossen, aber die Gate-Prüfung soll unabhängig davon immer am tatsächlich
  // eingeloggten Nutzer hängen — identisch zur UI-Verdrahtung in TravelExpensesPage).
  const foreignRates = getForeignRatesForUser(auth.currentUser?.uid ?? null, options?.tier ?? 'free');
  const calcResult = calculateTravelAllowances(calcInput, TRAVEL_RATES, { foreignRates });

  if (!calcResult.ok) {
    throw new TravelApiError(
      'calcFailed',
      `travelApi.settleTravelTrip: Berechnung fehlgeschlagen — ${calcResult.error}`,
      { calcError: calcResult.error },
    );
  }

  // NEU (§13.9, additiv, Reviewer F3): fail-closed Konsistenz-Guard VOR dem Write — reine
  // Redundanz-Absicherung zur bereits strukturell garantierten Kern-Invariante
  // (travelAllowance.ts setzt `foreignRatesVersionId` IMMER, sobald ≥1 Tag `rateSource` trägt).
  // Wenn mind. ein Tag im Snapshot `rateSource` trägt (Auslandssatz angewandt), MUSS
  // `foreignRatesVersionId` gesetzt sein — sonst wäre der Herkunftsnachweis des Snapshots
  // (§13.9 GoBD) lückenhaft. Kein Werfen im Kern nötig (Defense-in-Depth, V12-Muster), aber der
  // Snapshot darf in diesem (eigentlich unerreichbaren) Fall NICHT geschrieben werden.
  const anyForeignRateApplied = calcResult.days.some((day) => day.rateSource !== undefined);
  if (anyForeignRateApplied && calcResult.foreignRatesVersionId === undefined) {
    throw new TravelApiError(
      'snapshotInvalid',
      'travelApi.settleTravelTrip: Konsistenz-Guard verletzt (§13.9, fail-closed) — mindestens ' +
        'ein Tag trägt rateSource (Auslandssatz angewandt), aber foreignRatesVersionId fehlt im ' +
        'Berechnungs-Snapshot. Snapshot wird NICHT geschrieben.',
    );
  }

  // PFLICHT (§3.3c): Integer-Cent-/Number.isFinite-Prüfung des KOMPLETTEN Snapshots VOR dem
  // Write — zusätzlich zum kern-internen Abschluss-Guard (Regel 8), Defense-in-Depth (V12).
  const snapshotValidation = validateCalcSnapshotCents(calcResult);
  if (!snapshotValidation.valid) {
    throw new TravelApiError(
      'snapshotInvalid',
      `travelApi.settleTravelTrip: Berechnungs-Snapshot enthält ungültige Cent-Werte ` +
        `(fail-closed) — ${snapshotValidation.errors.join('; ')}`,
    );
  }

  // NEU (RK-22, E19): Settle-Zeit-Snapshot der verknüpften Ist-Kosten bauen — fail-closed VOR
  // dem Write. Nicht ladbare / nicht-'booked' Ausgaben ⇒ TravelApiError 'linkedExpenseUnavailable'
  // (kein Write); ungültige grossCents ⇒ 'snapshotInvalid'. Leeres Array ⇒ Key wird unten
  // WEGGELASSEN (V10). KEIN DATEV-Effekt — reine Anzeige-/Report-Schicht (V24/E18).
  const linkedExpenses = await buildLinkedExpensesSnapshot(trip.linkedExpenseIds);

  const settlement: TravelSettlement = {
    settledAtISO: new Date().toISOString(),
    ratesVersionId: calcResult.ratesVersionId,
    belegLabel: buildBelegLabel(trip.returnDateISO, id),
    calc: calcResult,
    // Key NUR aufnehmen, wenn ≥ 1 Verknüpfung existiert — nie `undefined`/`[]` materialisieren
    // (V10, damit die toEqual-Fixtures ohne Verknüpfung byte-identisch bleiben).
    ...(linkedExpenses.length > 0 ? { linkedExpenses } : {}),
  };

  assertNoUndefinedValues(settlement as unknown as Record<string, unknown>);

  const docRef = doc(db, TRAVEL_TRIPS_COLLECTION, id);
  await updateDoc(docRef, { status: 'settled', settlement } as Partial<TravelTrip>);

  // Fire-and-forget (best-effort, non-blocking — s. writeTravelAudit-Doc).
  void writeTravelAudit(uid, 'settle', id, {
    verpflegungCents: calcResult.verpflegungCents,
    mileageCents: calcResult.mileageCents,
    totalCents: calcResult.totalCents,
    belegLabel: settlement.belegLabel,
    linkedExpenseCount: linkedExpenses.length,
  });
}

// ─── cancelTravelTrip ───────────────────────────────────────────────────────────

/**
 * Storniert eine `settled`-Reise (einzige erlaubte Folgetransition nach `settled`, E7/V7).
 *
 * Schreibt exakt die drei Felder, die die Firestore-Rule per `hasOnly(['status',
 * 'cancellationReason', 'cancelledAtISO'])` erlaubt (§7) — kein weiteres Feld wird berührt.
 * MVP-Limitation (O-5): wirkt nur auf KÜNFTIGE DATEV-Exporte; bereits exportierte Pauschalen
 * brauchen eine Generalumkehr (RK-W2-4), hier nicht abgedeckt.
 *
 * @param id - Firestore document ID of the trip to cancel.
 * @param reason - Pflicht-Freitext-Begründung (non-empty nach `trim()`).
 * @throws {Error} If not authenticated, `reason` is empty, the trip does not exist, is not
 *   `settled`, or the Firestore write fails.
 */
export async function cancelTravelTrip(id: string, reason: string): Promise<void> {
  const uid = requireCurrentUser();

  const trimmedReason = reason.trim();
  if (trimmedReason.length === 0) {
    throw new TravelApiError('cancelReasonRequired', 'travelApi.cancelTravelTrip: reason darf nicht leer sein.');
  }

  const trip = await getTravelTrip(id);
  if (trip === null) {
    throw new TravelApiError('notFound', `travelApi.cancelTravelTrip: Reise ${id} nicht gefunden.`);
  }
  if (trip.status !== 'settled') {
    throw new TravelApiError(
      'wrongStatus',
      `travelApi.cancelTravelTrip: nur abgerechnete Reisen ('settled') können storniert werden ` +
        `(aktueller Status: '${trip.status}').`,
    );
  }

  const docRef = doc(db, TRAVEL_TRIPS_COLLECTION, id);
  await updateDoc(docRef, {
    status: 'cancelled',
    cancellationReason: trimmedReason,
    cancelledAtISO: new Date().toISOString(),
  } as Partial<TravelTrip>);

  // Fire-and-forget (best-effort, non-blocking — s. writeTravelAudit-Doc).
  void writeTravelAudit(uid, 'cancel', id, { cancellationReason: trimmedReason });
}

// ─── deleteTravelTrip ───────────────────────────────────────────────────────────

/**
 * Deletes a travel trip — only allowed while it is still a `draft` (Rules §7, GoBD).
 *
 * @param id - Firestore document ID of the trip to delete.
 * @throws {Error} If not authenticated, the trip does not exist, is not `draft` (permission-
 *   denied at the server, pre-checked here for UX), or the Firestore delete fails.
 */
export async function deleteTravelTrip(id: string): Promise<void> {
  const uid = requireCurrentUser();

  const trip = await getTravelTrip(id);
  if (trip === null) {
    throw new TravelApiError('notFound', `travelApi.deleteTravelTrip: Reise ${id} nicht gefunden.`);
  }
  if (trip.status !== 'draft') {
    throw new TravelApiError(
      'wrongStatus',
      `travelApi.deleteTravelTrip: nur Reisen im Status 'draft' können gelöscht werden ` +
        `(aktueller Status: '${trip.status}').`,
    );
  }

  const docRef = doc(db, TRAVEL_TRIPS_COLLECTION, id);
  await deleteDoc(docRef);

  // Fire-and-forget (best-effort, non-blocking — s. writeTravelAudit-Doc).
  void writeTravelAudit(uid, 'delete', id, {});
}
